import { useAuthToken } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/utils/appsettings';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { getWorkspaceQueryString } from '@/utils/workspaceUtils';
import { totalAvailableHours as computeTotalAvailableHours } from '@/utils/expertHours';
import { fetchBillingSummary, invalidateBillingSummary } from '@/lib/billingSummary';

interface ArchitectHours {
    monthly_allocated: number;
    monthly_used: number;
    add_on_balance: number;
}

interface ExpertMessage {
    id: number;
    request_id: number;
    sender_id: number;
    sender_name: string;
    sender_role: string;
    content: string;
    created_at?: string;
    timestamp?: string;
}

interface EngagementRequest {
    request_id: string;
    project_id: string;
    project_name?: string | null;
    project_type?: string | null;
    status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
    created_at: string;
    sla_deadline: string;
    developer_name?: string;
    used_hours?: number;
    total_hours_budget?: number;
    remaining_hours?: number;
    category?: string | null;
    request_description?: string | null;
    is_active?: boolean;
    is_read_only?: boolean;
    is_visible_to_owner?: boolean;
}

export const useEngagement = (projectId?: string) => {
    const token = useAuthToken();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { activeWorkspace } = useWorkspace();

    // 1. Fetch Balance
    const { data: hours, refetch: refresh, isLoading: loadingBalance } = useQuery({
        queryKey: ['billing-summary', activeWorkspace?.id],
        queryFn: async () => {
            const data = await fetchBillingSummary(getWorkspaceQueryString(activeWorkspace), { token });
            return data.architect_hours as ArchitectHours;
        },
        enabled: !!token && !!activeWorkspace?.id,
        // Every chat Message mounts this hook; with the default staleTime of 0 each mount refetched (UAT 1030imp1)
        staleTime: 30_000,
    });

    // 2. Fetch Requests
    const { data: requests = [], refetch: fetchRequests, isLoading: loadingRequests } = useQuery({
        queryKey: ['engagement-requests'],
        queryFn: async () => {
            const response = await axiosInstance.get('/engagement/requests', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.data as EngagementRequest[];
        },
        enabled: !!token
    });

    // A writable engagement is one that is not completed/cancelled. Used to gate write
    // actions (send message, re-engage button state).
    const activeRequest = requests.find(r => r.project_id === projectId && !['completed', 'cancelled'].includes(r.status));

    // Project-scoped: does ANY user have an active engagement on this project? The `requests`
    // list above (and therefore `activeRequest`) only sees the CURRENT user's own engagements
    // (GET /engagement/requests is owner-scoped), so a project collaborator other than the
    // original requester would otherwise see no activeRequest and be able to start a second,
    // conflicting purchase/expert assignment on a project that already has one. Mirrors the
    // server-side guard in POST /engagement/request.
    const { data: projectActiveCheck } = useQuery({
        queryKey: ['engagement-check-active', projectId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/engagement/check-active/${projectId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.data as { has_active_engagement: boolean; request_id: string | null; status: string | null };
        },
        enabled: !!token && !!projectId
    });

    // Use these to gate a NEW purchase; `activeRequest` stays owner-scoped for callers that
    // need the full record (messages, board).
    const hasActiveEngagement = !!activeRequest || !!projectActiveCheck?.has_active_engagement;
    const activeEngagementStatus = activeRequest?.status ?? projectActiveCheck?.status ?? null;

    // A visible engagement is the one the owner can still see: active OR completed
    // (read-only history). Cancelled engagements stay hidden. History (messages, tasks,
    // logs) binds to this so a completed engagement keeps its conversation and activity.
    const visibleRequest = requests.find(r => r.project_id === projectId && r.status !== 'cancelled');

    // Read-only when the visible engagement is completed (or the backend marks it so). In
    // this state the board/chat/logs render but every write control is disabled.
    const readOnly = !!visibleRequest && (visibleRequest.is_read_only ?? visibleRequest.status === 'completed');

    // 3. Fetch Messages (Poll every 5s). Bound to the visible engagement so completed
    // engagements keep showing their full conversation history in read-only mode.
    const { data: expertMessages = [] } = useQuery({
        queryKey: ['expert-messages', visibleRequest?.request_id],
        queryFn: async () => {
            const response = await axiosInstance.get(`/engagement/${visibleRequest?.request_id}/messages`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.data as ExpertMessage[];
        },
        enabled: !!token && !!visibleRequest,
        // Stop polling once read-only: history no longer changes.
        refetchInterval: readOnly ? false : 5000,
        refetchOnWindowFocus: false, // Don't refetch immediately on focus, rely on interval or mutation
        staleTime: 4000
    });

    // 4. Send Message Mutation
    const { mutateAsync: sendExpertMessage, isPending: sendingMessage } = useMutation({
        mutationFn: async (content: string) => {
            const response = await axiosInstance.post(
                `/engagement/${activeRequest?.request_id}/messages`,
                { content },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            return response.data;
        },
        onSuccess: (newMessage) => {
            // Optimistic update or just invalidate. activeRequest === visibleRequest while
            // writable, so this key matches the messages query above.
            queryClient.setQueryData(['expert-messages', visibleRequest?.request_id], (old: ExpertMessage[] | undefined) => {
                return old ? [...old, newMessage] : [newMessage];
            });
        },
        onError: (err: any) => {
            const msg = err.response?.data?.detail || err.message || "Failed to send message";
            toast({ variant: "destructive", title: "Error", description: msg });
        }
    });

    // 5. Request Engagement Mutation
    // Accepts the chosen hours (multi-project incremental redemption) plus an optional
    // guided category + description. A bare number is still accepted for backward compat.
    const { mutateAsync: requestEngagement, isPending: requesting } = useMutation({
        mutationFn: async (
            input: number | { hours: number; category?: string; description?: string; serviceType?: string; projectId?: string }
        ) => {
            const payload = typeof input === 'number' ? { hours: input, serviceType: undefined, projectId: undefined } : input;
            const response = await axiosInstance.post(
                '/engagement/request',
                {
                    // A project created mid-flow (standalone migration) is passed per call
                    project_id: payload.projectId ?? projectId,
                    hours: payload.hours,
                    category: payload.category,
                    description: payload.description,
                    // Fixed-scope services seed their own Kanban checklist server-side.
                    service_type: payload.serviceType,
                    // Deduct from the SAME workspace subscription the balance is shown for.
                    workspace_id: activeWorkspace?.id,
                },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            return response.data;
        },
        onSuccess: () => {
            toast({
                title: "Request Submitted",
                description: "A developer will be assigned shortly.",
            });
            queryClient.invalidateQueries({ queryKey: ['engagement-requests'] });
            invalidateBillingSummary();
            queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
        },
        onError: (err: any) => {
            const msg = err.response?.data?.detail || err.message || "Failed to submit request";
            toast({ variant: "destructive", title: "Error", description: msg });
        }
    });

    const totalAvailableHours = computeTotalAvailableHours(hours);

    return {
        hours: hours || null,
        totalAvailableHours,
        requests,
        activeRequest,
        hasActiveEngagement,
        activeEngagementStatus,
        visibleRequest,
        readOnly,
        expertMessages,
        loading: loadingBalance || loadingRequests,
        requesting,
        sendingMessage,
        requestEngagement,
        sendExpertMessage,
        fetchRequests,
        refresh
    };
};
